import { collection, doc, getDoc, getDocs, setDoc, query, where, Timestamp } from 'firebase/firestore';
import { db } from '../firebase';

const SCHOOLS_COLLECTION = 'schools';

/**
 * SchoolService - Manages school entities and related operations
 */
class SchoolService {
  /**
   * Get a school by ID
   * @param {string} schoolId - The school ID
   * @returns {Promise<Object|null>} School data or null if not found
   */
  async getSchool(schoolId) {
    try {
      const schoolRef = doc(db, SCHOOLS_COLLECTION, schoolId);
      const schoolSnap = await getDoc(schoolRef);

      if (schoolSnap.exists()) {
        return {
          id: schoolSnap.id,
          ...schoolSnap.data()
        };
      }

      return null;
    } catch (error) {
      console.error('[SchoolService] Error getting school:', error);
      throw error;
    }
  }

  /**
   * Get all schools (admin only)
   * @returns {Promise<Array>} Array of school objects
   */
  async getAllSchools() {
    try {
      const schoolsRef = collection(db, SCHOOLS_COLLECTION);
      const snapshot = await getDocs(schoolsRef);

      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('[SchoolService] Error getting all schools:', error);
      throw error;
    }
  }

  /**
   * Create a new school
   * @param {Object} schoolData - School data (name, slug, settings)
   * @returns {Promise<Object>} Created school with ID
   */
  async createSchool(schoolData) {
    try {
      const { name, slug, settings = {} } = schoolData;

      if (!name || !slug) {
        throw new Error('School name and slug are required');
      }

      // Check if a school with this name already exists (case-insensitive)
      const existingByName = await this.getSchoolByName(name);
      if (existingByName) {
        throw new Error(`A school named "${existingByName.name}" already exists`);
      }

      // Check if slug already exists
      const existingBySlug = await this.getSchoolBySlug(slug);
      if (existingBySlug) {
        throw new Error(`School with slug "${slug}" already exists`);
      }

      const schoolRef = doc(collection(db, SCHOOLS_COLLECTION));
      const newSchool = {
        name,
        slug,
        settings,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        active: true
      };

      await setDoc(schoolRef, newSchool);

      return {
        id: schoolRef.id,
        ...newSchool
      };
    } catch (error) {
      console.error('[SchoolService] Error creating school:', error);
      throw error;
    }
  }

  /**
   * Get a school by slug
   * @param {string} slug - The school slug
   * @returns {Promise<Object|null>} School data or null if not found
   */
  async getSchoolBySlug(slug) {
    try {
      const schoolsRef = collection(db, SCHOOLS_COLLECTION);
      const q = query(schoolsRef, where('slug', '==', slug));
      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        return {
          id: doc.id,
          ...doc.data()
        };
      }

      return null;
    } catch (error) {
      console.error('[SchoolService] Error getting school by slug:', error);
      throw error;
    }
  }

  /**
   * Get a school by exact name (case-insensitive)
   * @param {string} name - The school name
   * @returns {Promise<Object|null>} School data or null if not found
   */
  async getSchoolByName(name) {
    try {
      const normalizedName = name.trim().toLowerCase();
      const schoolsRef = collection(db, SCHOOLS_COLLECTION);
      const snapshot = await getDocs(schoolsRef);

      // Client-side filtering for case-insensitive name match
      for (const doc of snapshot.docs) {
        const schoolData = doc.data();
        if (schoolData.name && schoolData.name.trim().toLowerCase() === normalizedName) {
          return {
            id: doc.id,
            ...schoolData
          };
        }
      }

      return null;
    } catch (error) {
      console.error('[SchoolService] Error getting school by name:', error);
      throw error;
    }
  }

  /**
   * Update school data
   * @param {string} schoolId - The school ID
   * @param {Object} updates - Fields to update
   * @returns {Promise<void>}
   */
  async updateSchool(schoolId, updates) {
    try {
      const schoolRef = doc(db, SCHOOLS_COLLECTION, schoolId);
      await setDoc(schoolRef, {
        ...updates,
        updatedAt: Timestamp.now()
      }, { merge: true });
    } catch (error) {
      console.error('[SchoolService] Error updating school:', error);
      throw error;
    }
  }

  /**
   * Create a default school for initial setup or migration
   * @param {string} name - Optional school name (defaults to "Default School")
   * @returns {Promise<Object>} Created school with ID
   */
  async createDefaultSchool(name = 'Default School') {
    try {
      // Check if default school already exists
      const existingSchool = await this.getSchoolBySlug('default');
      if (existingSchool) {
        console.log('[SchoolService] Default school already exists');
        return existingSchool;
      }

      return await this.createSchool({
        name,
        slug: 'default',
        settings: {
          timezone: 'UTC',
          locale: 'en-US'
        }
      });
    } catch (error) {
      console.error('[SchoolService] Error creating default school:', error);
      throw error;
    }
  }
}

// Export singleton instance
export default new SchoolService();
